import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { template } from 'lodash';
import { of, zip } from 'rxjs';
import { catchError, map, mergeMap, repeat, switchMap, tap } from 'rxjs/operators';

import { AnalyticsFacade } from '@amfam/shared/analytics';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { Choice, CopyService } from '@amfam/shared/utility/shared-services';

import * as fromRoot from '../../core/store';
import { AddressChange, ChangeRequest } from '../models';
import { PlchangerequestService } from '../services/plchangerequest.service';
import {
  addressChangeAgentEmail,
  addressChangeAgentEmailFailed,
  addressChangeAgentEmailSuccess,
  addressChangeANS,
  addressChangeANSFailed,
  addressChangeANSSuccess,
  addressChangeCustomerEmail,
  addressChangeNotification,
  addressChangeNotificationFailed,
  driverLicenseChangeANS,
  driverLicenseChangeANSFailed,
  driverLicenseChangeANSSuccess,
  driverLicenseChangeCustomerEmail,
  PolicyChangeRequestANSNotificationAction,
  PolicyChangeRequestEmailNotificationAction,
  PolicyChangeRequestFailANSNotificationAction,
  PolicyChangeRequestFailEmailNotificationAction,
  PolicyChangeRequestNotificationAction,
  PolicyChangeRequestNotificationFailAction,
  PolicyChangeRequestSendAnalyticAction,
  PolicyChangeRequestSuccessANSNotificationAction,
  PolicyChangeRequestSuccessEmailNotificationAction,
  vinChangeAgentEmail,
  vinChangeAgentEmailFailed,
  vinChangeAgentEmailSuccess,
  vinChangeANS,
  vinChangeANSFailed,
  vinChangeANSSuccess,
  vinChangeCustomerEmail,
  vinChangeNotification,
  vinChangeNotificationFailed
} from './policy-change.actions';

@Injectable()
export class PolicyChangeEffects {
  constructor(
    private rootStore: Store<fromRoot.RootState>,
    private plService: PlchangerequestService,
    private action$: Actions,
    private analyticFacade: AnalyticsFacade,
    private copyService: CopyService
  ) {}

  /**
   * @description:
   */
  notificationSplitter$ = createEffect(() =>
    this.action$.pipe(
      ofType(PolicyChangeRequestNotificationAction),
      map(action => action.payload),
      mergeMap(payload => {
        return [
          PolicyChangeRequestEmailNotificationAction({ payload }),
          PolicyChangeRequestANSNotificationAction({ payload })
        ];
      }),
      catchError(error => of(PolicyChangeRequestNotificationFailAction(error)))
    )
  );

  /**
   * @description:
   */

  changePolicyDetailsEmailRequest$ = createEffect(() =>
    this.action$.pipe(
      ofType(PolicyChangeRequestEmailNotificationAction),
      map(action => action.payload),
      mergeMap(payload =>
        this.plService.sendAgentEmailNotifications(payload).pipe(
          map(() => PolicyChangeRequestSuccessEmailNotificationAction({ payload })),
          catchError(error => of(PolicyChangeRequestNotificationFailAction(error)))
        )
      ),
      catchError(error => of(PolicyChangeRequestFailEmailNotificationAction(error)))
    )
  );

  /**
   * @description:
   */

  changePolicyDetailsANSRequest$ = createEffect(() =>
    this.action$.pipe(
      ofType(PolicyChangeRequestANSNotificationAction),
      map(action => action.payload),
      switchMap(incomingPayload =>
        this.plService.sendANSNotifications(incomingPayload).pipe(
          map(response => PolicyChangeRequestSuccessANSNotificationAction(response)),
          catchError(error => of(PolicyChangeRequestNotificationFailAction(error)))
        )
      ),
      catchError(error => of(PolicyChangeRequestFailANSNotificationAction(error)))
    )
  );

  /**
   * @description:
   */

  changePolicyDetailsSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(PolicyChangeRequestSuccessEmailNotificationAction),
        map(action => action.payload),
        switchMap(incomingPayload => this.plService.generateCustomerEmail(incomingPayload))
      ),
    { dispatch: false }
  );

  /**
   * @description:
   */

  sendPolicyAnalytics$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(PolicyChangeRequestSendAnalyticAction),
        map(action => action.payload),
        map(incomingPayload => this.plService.sendAnalytic(incomingPayload))
      ),
    { dispatch: false }
  );

  addressChangeNotificationSplitter$ = createEffect(() =>
    this.action$.pipe(
      ofType(addressChangeNotification),
      map(action => action.payload),
      mergeMap(payload => {
        return [addressChangeAgentEmail({ payload }), addressChangeANS({ payload })];
      }),
      catchError(error => of(addressChangeNotificationFailed(error)))
    )
  );

  addressChangeAgentEmail$ = createEffect(() =>
    this.action$.pipe(
      ofType(addressChangeAgentEmail),
      map(action => action.payload),
      switchMap(payload =>
        this.plService.sendAgentEmail(payload).pipe(
          map(() => addressChangeAgentEmailSuccess({ payload })),
          catchError(error => of(addressChangeAgentEmailFailed({ payload, status: error })))
        )
      ),
      catchError(error => of(addressChangeAgentEmailFailed({ payload: undefined, status: error })))
    )
  );

  addressChangeANS$ = createEffect(() =>
    this.action$.pipe(
      ofType(addressChangeANS),
      map(action => action.payload),
      switchMap(payload =>
        this.plService.sendANS(payload).pipe(
          map(() => addressChangeANSSuccess({ payload })),
          catchError(error => of(addressChangeANSFailed({ payload, status: error })))
        )
      ),
      catchError(error => of(addressChangeANSFailed({ payload: undefined, status: error })))
    )
  );

  private routeToConfirmation(agentId: string) {
    return fromRouterActions.Go({
      path: ['/policies/changerequest/confirmation'],
      extras: { state: { agentId: agentId } }
    });
  }

  addressChangeCustomerEmail$ = createEffect(() =>
    this.action$.pipe(
      ofType(addressChangeCustomerEmail),
      map(action => action.payload),
      switchMap(payload =>
        this.plService.sendCustomerEmail(payload).pipe(
          map(() => this.routeToConfirmation(payload.addressChange.agentId)),
          catchError(() => of(this.routeToConfirmation(payload.addressChange.agentId)))
        )
      )
    )
  );

  addressChangeError$ = createEffect(() =>
    zip(
      this.action$.pipe(ofType(addressChangeAgentEmailFailed)),
      this.action$.pipe(ofType(addressChangeANSFailed))
    ).pipe(
      map(actions => actions[0]),
      tap(data => {
        const analytics = this.copyService.getCopy('policy.addressChange.analytics');
        this.analyticFacade.trackEvent({
          event: analytics.failure.event,
          address_type: this.getAddressTypes(data.payload?.addressChange)
        });
      }),
      map(data => addressChangeNotificationFailed({ payload: data.status }))
    )
  );

  private getAddressTypes(addressChange: AddressChange): string {
    const addressType = this.copyService.getCopy('policy.addressChange.analytics.addressTypes');
    const addressTypes = [];
    if (addressChange?.sendDocuments?.toUpperCase() === Choice.YES.toUpperCase())
      addressTypes.push(addressType.mailing);
    if (addressChange?.vehiclesGaraged?.toUpperCase() === Choice.YES.toUpperCase())
      addressTypes.push(addressType.parking);

    return addressTypes.length > 0 ? addressTypes.join(', ') : 'N/A';
  }

  addressChangeSuccessEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(addressChangeANSSuccess, addressChangeAgentEmailSuccess),
      map(action => action.payload),
      tap(payload => {
        const analytics = this.copyService.getCopy('policy.addressChange.analytics');
        this.analyticFacade.trackEvent({
          event: analytics.complete.event,
          address_type: this.getAddressTypes(payload.addressChange)
        });
      }),
      switchMap(payload => of(addressChangeCustomerEmail({ payload }))),
      repeat()
    )
  );

  driverLicenseChangeSuccessEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(driverLicenseChangeANSSuccess),
      map(action => action.payload),
      tap(payload => {
        const analytics = this.copyService.getCopy('policy.driverLicenseChange.analytics');
        this.analyticFacade.trackEvent({
          event: analytics.complete.event
        });
      }),
      switchMap(payload => of(driverLicenseChangeCustomerEmail({ payload })))
    )
  );

  driverLicenseChangeANS$ = createEffect(() =>
    this.action$.pipe(
      ofType(driverLicenseChangeANS),
      map(action => action.payload),
      switchMap(payload =>
        this.plService.sendANS(payload).pipe(
          map(() => driverLicenseChangeANSSuccess({ payload })),
          catchError(error => {
            return of(driverLicenseChangeANSFailed({ payload: error }));
          })
        )
      ),
      catchError(error => {
        return of(driverLicenseChangeANSFailed({ payload: error }));
      })
    )
  );
  driverLicenseChangeANSFailed$ = createEffect(
    () =>
      this.action$.pipe(ofType(driverLicenseChangeANSFailed)).pipe(
        tap(() => {
          const analytics = this.copyService.getCopy('policy.driverLicenseChange.analytics');
          this.analyticFacade.trackEvent({
            event: analytics.failure.event
          });
        })
      ),
    { dispatch: false }
  );

  driverLicenseChangeCustomerEmail$ = createEffect(() =>
    this.action$.pipe(
      ofType(driverLicenseChangeCustomerEmail),
      map(action => action.payload),
      switchMap(payload =>
        this.plService.sendCustomerEmail(payload).pipe(
          map(() => this.routeToConfirmation(payload.driverLicenseChange.agentId)),
          catchError(() => of(this.routeToConfirmation(payload.driverLicenseChange.agentId)))
        )
      )
    )
  );

  // Vin Change

  vinChangeNotificationSplitter$ = createEffect(() =>
    this.action$.pipe(
      ofType(vinChangeNotification),
      map(action => action.payload),
      mergeMap(payload => {
        return [vinChangeAgentEmail({ payload }), vinChangeANS({ payload })];
      }),
      catchError(error => of(vinChangeNotificationFailed(error)))
    )
  );

  vinChangeAgentEmail$ = createEffect(() =>
    this.action$.pipe(
      ofType(vinChangeAgentEmail),
      map(action => action.payload),
      switchMap(payload =>
        this.plService.sendAgentEmail(payload).pipe(
          map(() => vinChangeAgentEmailSuccess({ payload })),
          catchError(error => of(vinChangeAgentEmailFailed({ payload, status: error })))
        )
      ),
      catchError(error => of(vinChangeAgentEmailFailed({ payload: undefined, status: error })))
    )
  );

  vinChangeANS$ = createEffect(() =>
    this.action$.pipe(
      ofType(vinChangeANS),
      map(action => action.payload),
      switchMap(payload =>
        this.plService.sendANS(payload).pipe(
          map(() => vinChangeANSSuccess({ payload })),
          catchError(error => of(vinChangeANSFailed({ payload, status: error })))
        )
      ),
      catchError(error => of(addressChangeANSFailed({ payload: undefined, status: error })))
    )
  );

  vinChangeCustomerEmail$ = createEffect(() =>
    this.action$.pipe(
      ofType(vinChangeCustomerEmail),
      map(action => action.payload),
      switchMap(payload =>
        this.plService.sendCustomerEmail(payload).pipe(
          map(() => this.routeToConfirmation(payload.requestChange.agentId)),
          catchError(() => of(this.routeToConfirmation(payload.requestChange.agentId)))
        )
      )
    )
  );

  vinChangeError$ = createEffect(() =>
    zip(
      this.action$.pipe(ofType(vinChangeAgentEmailFailed)),
      this.action$.pipe(ofType(vinChangeANSFailed))
    ).pipe(
      map(actions => actions[0]),
      tap(data => {
        const analytics = this.copyService.getCopy('policy.vinChange.analytics');
        this.analyticFacade.trackEvent({
          event: analytics.failure.event
        });
      }),
      map(data => vinChangeNotificationFailed({ payload: data.status }))
    )
  );

  vinChangeSuccessEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(vinChangeANSSuccess, vinChangeAgentEmailSuccess),
      map(action => action.payload),
      tap(payload => {
        const analytics = this.copyService.getCopy('policy.vinChange.analytics');
        this.analyticFacade.trackEvent(this.getuccessAnalyitcs(payload, analytics));
      }),
      switchMap(payload => of(vinChangeCustomerEmail({ payload }))),
      repeat()
    )
  );

  private getuccessAnalyitcs(payload: ChangeRequest, analytics) {
    const eventInfo: any = {};
    if (payload.vinChange.make) {
      const completeMSRP = analytics.completeMSRP;
      eventInfo.event = completeMSRP.event;
      eventInfo['vehicleYear'] = template(completeMSRP.vehicleYear)({
        vehicle_year: payload.vinChange.newYear || ''
      });
      eventInfo['vehicleMake'] = template(completeMSRP.vehicleMake)({
        vehicle_make: payload.vinChange.make || ''
      });
      eventInfo['vehicleModel'] = template(completeMSRP.vehicleModel)({
        vehicle_model: payload.vinChange.model || ''
      });
    } else eventInfo.event = analytics.complete.event;
    return eventInfo;
  }
}
